import { AxiosResponse } from 'axios';
import makeRequest from './client';
import { HttpStatus } from './httpStatus';
import { LabelSeparationPutBody } from './labelSeparation';
import {
  LabelSeparationDetail,
  LabelSeparationDetailResult
} from '../components/Common/LabelSeparation/types';
import {
  SectionSeparationDetail,
  SectionSeparationDetailResult,
  SectionSeparationPutBody
} from '../components/Common/SectionSeparation/types';
import {
  SectionMappingDetail,
  SectionMappingDetailResult,
  SectionMappingPutBody
} from '../components/Common/SectionMapping/types';

/**
 * Shape of the JSON body returned by all backend API endpoints.
 * `Success` varies per endpoint and is intentionally `unknown` here;
 * each resolver casts it to the concrete detail type it expects.
 */
type ApiResponseData = {
  error?: boolean;
  message?: string;
  Success?: unknown;
};

/**
 * What `makeRequest` resolves to: an Axios response, or null / undefined
 * when the request fails entirely (network error with no server response).
 */
type ApiResponse = AxiosResponse<ApiResponseData> | null | undefined;

const DEFAULT_ERROR_MESSAGE = 'Failed to load label separation detail';
const DEFAULT_SECTION_ERROR_MESSAGE = 'Failed to load section separation detail';
const DEFAULT_SECTION_MAPPING_ERROR_MESSAGE = 'Failed to load section mapping detail';

export const getBrazilLabelSeparationDetail = (documentId: number) =>
  makeRequest(`/brazil/label_separation/${documentId}`, 'GET');

export const saveBrazilLabelSeparation = (documentId: number, body: LabelSeparationPutBody) =>
  makeRequest(`/brazil/label_separation/${documentId}`, 'PUT', body);

export const resolveBrazilLabelSeparationDetail = (
  response: ApiResponse
): LabelSeparationDetailResult => {
  if (!response) {
    return {
      ok: false,
      message: 'Unable to reach the server. Please try again.'
    };
  }

  const detail = response?.data?.Success as LabelSeparationDetail | undefined;
  if (detail?.document_id !== undefined) {
    return { ok: true, data: detail };
  }

  if (response.status === HttpStatus.NOT_FOUND) {
    return { ok: false, message: 'Document not found' };
  }

  const message = response?.data?.message;
  if (typeof message === 'string' && message.trim()) {
    return { ok: false, message };
  }

  if (response.status) {
    return { ok: false, message: `${DEFAULT_ERROR_MESSAGE} (${response.status})` };
  }

  return { ok: false, message: DEFAULT_ERROR_MESSAGE };
};

export const getBrazilSectionSeparation = (documentId: number) =>
  makeRequest(`/brazil/section_separation/${documentId}`, 'GET');

export const saveBrazilSectionSeparation = (documentId: number, body: SectionSeparationPutBody) =>
  makeRequest(`/brazil/section_separation/${documentId}`, 'PUT', body);

export const resolveBrazilSectionSeparationDetail = (
  response: ApiResponse
): SectionSeparationDetailResult => {
  if (!response) {
    return {
      ok: false,
      message: 'Unable to reach the server. Please try again.'
    };
  }

  const detail = (response?.data?.Success as { result?: SectionSeparationDetail } | undefined)
    ?.result;
  if (detail?.document_link) {
    return { ok: true, data: detail };
  }

  if (response.status === HttpStatus.NOT_FOUND) {
    return { ok: false, message: 'Document has no EN PDF' };
  }

  const message = response?.data?.message;
  if (typeof message === 'string' && message.trim()) {
    return { ok: false, message };
  }

  if (response.status) {
    return { ok: false, message: `${DEFAULT_SECTION_ERROR_MESSAGE} (${response.status})` };
  }

  return { ok: false, message: DEFAULT_SECTION_ERROR_MESSAGE };
};

export const triggerBrazilDag = () => makeRequest('/brazil/dag/trigger', 'GET');

export const getBrazilSectionMapping = (documentId: number) =>
  makeRequest(`/brazil/section_mapping/${documentId}`, 'GET');

export const saveBrazilSectionMapping = (documentId: number, body: SectionMappingPutBody) =>
  makeRequest(`/brazil/section_mapping/${documentId}`, 'PUT', body);

export const resolveBrazilSectionMappingDetail = (
  response: ApiResponse
): SectionMappingDetailResult => {
  if (!response) {
    return {
      ok: false,
      message: 'Unable to reach the server. Please try again.'
    };
  }

  const detail = response?.data?.Success as SectionMappingDetail | undefined;
  if (detail?.s3_path) {
    return { ok: true, data: detail };
  }

  if (response.status === HttpStatus.NOT_FOUND) {
    return { ok: false, message: 'Document not found' };
  }

  const message = response?.data?.message;
  if (typeof message === 'string' && message.trim()) {
    return { ok: false, message };
  }

  if (response.status) {
    return { ok: false, message: `${DEFAULT_SECTION_MAPPING_ERROR_MESSAGE} (${response.status})` };
  }

  return { ok: false, message: DEFAULT_SECTION_MAPPING_ERROR_MESSAGE };
};
